import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  Paper,
  Stack,
  Avatar,
  Container,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert
} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { supabase } from '../../supabaseClient'
import { ChromePicker } from 'react-color'
import { useNavigate, useParams } from 'react-router-dom'
import { getSignedImageUrl } from '../../constants/CommonFunctions'
import { OFFICES } from '../../routes/routes'

const colorOptions = [
  { label: 'Azul oscuro', value: '#003366' },
  { label: 'Azul claro', value: '#3498db' },
  { label: 'Verde esmeralda', value: '#2ecc71' },
  { label: 'Verde oliva', value: '#808000' },
  { label: 'Gris neutro', value: '#7f8c8d' },
  { label: 'Negro elegante', value: '#000000' },
  { label: 'Rojo burdeos', value: '#8e0e00' },
  { label: 'Morado corporativo', value: '#6a0dad' },
  { label: 'Naranja suave', value: '#f39c12' },
  { label: 'Personalizar...', value: 'custom' }
]

const OfficeRegisterView = () => {
  const navigate = useNavigate();
  const { officeId } = useParams();
  const isEditMode = Boolean(officeId);
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))

  const [form, setForm] = useState({
    name: '',
    cif: '',
    phone: '',
    email: '',
    location: '',
    color: '',
    icon: null
  })

  const [iconPreview, setIconPreview] = useState(null)
  const [customColor, setCustomColor] = useState('#6A0DAD')
  const [loading, setLoading] = useState(false)
  const [loadingOffice, setLoadingOffice] = useState(isEditMode)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (!isEditMode) return;

    const fetchOffice = async () => {
      setLoadingOffice(true)
      setError(null)
      try {
        const { data, error: fetchError } = await supabase
          .from('office')
          .select('*')
          .eq('id', officeId)
          .maybeSingle()

        if (fetchError || !data) {
          throw new Error(fetchError?.message || 'No se pudo encontrar el despacho')
        }

        setForm({
          name: data.name || '',
          cif: data.document_number || '',
          phone: data.contact_number || '',
          email: data.email || '',
          location: data.location || '',
          color: data.color || '',
          icon: null
        })

        if (data.color && !colorOptions.some((o) => o.value === data.color)) {
          setCustomColor(data.color)
        }

        if (data.icon_url) {
          try {
            const signedUrl = await getSignedImageUrl(data.icon_url)
            setIconPreview(signedUrl)
          } catch (iconError) {
            console.error('Error obteniendo el ícono actual:', iconError)
          }
        }
      } catch (fetchError) {
        console.error('Error al cargar el despacho:', fetchError)
        setError(fetchError.message || 'Error al cargar el despacho')
      } finally {
        setLoadingOffice(false)
      }
    }

    fetchOffice()
  }, [isEditMode, officeId])

  const handleChange = (field) => (event) => {
    const value = event.target.value
    setForm({ ...form, [field]: value })

    if (field === 'color' && value === 'custom') {
      setForm({ ...form, color: customColor })
    }
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    setError(null)
    
    if (file) {
      // Validar tipo de archivo
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        setError('Solo se permiten archivos de imagen (JPG, PNG, GIF, WEBP)')
        return
      }

      // Validar tamaño (máximo 2MB)
      const maxSize = 2 * 1024 * 1024 // 2MB en bytes
      if (file.size > maxSize) {
        setError('El archivo debe ser menor a 2MB')
        return
      }

      setForm({ ...form, icon: file })
      
      // Crear preview
      const reader = new FileReader()
      reader.onload = () => setIconPreview(reader.result)
      reader.readAsDataURL(file)
    }
  }

  const handleCustomColorChange = (color) => {
    setCustomColor(color.hex)
    setForm((prev) => ({ ...prev, color: color.hex }))
  }

  const uploadIcon = async (officeId, iconFile) => {
    try {
      // Generar nombre único para el archivo
      const fileExtension = iconFile.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExtension}`
      
      // Definir la ruta del archivo
      const filePath = `office/${officeId}/icons/${fileName}`
      
      // Subir archivo a Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('files') // Usando el bucket 'files' según tu configuración
        .upload(filePath, iconFile, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) {
        throw new Error(`Error al subir archivo: ${uploadError.message}`)
      }

      // Obtener URL pública del archivo
      /* const { data: urlData } = supabase.storage
        .from('files')
        .getPublicUrl(filePath)

      return urlData.publicUrl */

      return filePath;
    } catch (error) {
      console.error('Error en uploadIcon:', error)
      throw error
    }
  }

  const handleUpdateOffice = async () => {
    // 1. Objeto con los campos editables
    const officeObj = {
      name: form.name,
      document_number: form.cif,
      contact_number: form.phone,
      email: form.email,
      location: form.location,
      color: form.color,
    }

    // 2. Subir ícono nuevo si el usuario eligió uno
    if (form.icon) {
      try {
        const iconUrl = await uploadIcon(officeId, form.icon)
        officeObj.icon_url = iconUrl
      } catch (iconError) {
        console.error('Error al subir ícono:', iconError)
        // Continuamos sin actualizar el ícono si falla la subida
      }
    }

    // 3. Actualizar office en base de datos
    const { error: officeError } = await supabase
      .from('office')
      .update(officeObj)
      .eq('id', officeId)

    if (officeError) {
      throw new Error(`Error al actualizar el despacho: ${officeError.message}`)
    }

    setSuccess(true)
    navigate(OFFICES)
  }

  const handleCreateOffice = async () => {
    // 1. Verificar usuario autenticado
    const user = (await supabase.auth.getUser()).data.user
    if (!user) {
      throw new Error('Usuario no autenticado')
    }

    // 2. Obtener organización del usuario
    const { data: orgData, error: orgError } = await supabase
      .from('organization')
      .select('id')
      .eq('owner_id', user.id)
      .maybeSingle()

    if (orgError || !orgData) {
      throw new Error('No se pudo encontrar la organización a la que pertenece')
    }

    // 3. Crear objeto office (sin icon_url inicialmente)
    const officeObj = {
      organization_id: orgData.id,
      created_by: user.id,
      name: form.name,
      document_number: form.cif,
      contact_number: form.phone,
      email: form.email,
      location: form.location,
      color: form.color,
      icon_url: null // Se actualizará después si hay ícono
    }

    // 4. Insertar office en base de datos
    const { data: officeData, error: officeError } = await supabase
      .from('office')
      .insert(officeObj)
      .select()
      .maybeSingle()

    if (officeError || !officeData) {
      throw new Error(`Error al registrar la oficina: ${officeError?.message}`)
    }

    // 5. Subir ícono si existe
    if (form.icon) {
      try {
        const iconUrl = await uploadIcon(officeData.id, form.icon)

        // 6. Actualizar office con la URL del ícono
        const { error: updateError } = await supabase
          .from('office')
          .update({ icon_url: iconUrl })
          .eq('id', officeData.id)

        if (updateError) {
          console.error('Error al actualizar URL del ícono:', updateError)
          // No lanzamos error aquí porque la oficina ya fue creada
        }
      } catch (iconError) {
        console.error('Error al subir ícono:', iconError)
        // Continuamos sin ícono si falla la subida
      }
    }

    // 7. Asignar usuario como administrador
    const memberObj = {
      office_id: officeData.id,
      member_id: user.id,
      role: 'admin'
    }

    const { error: memberError } = await supabase
      .from('office_member')
      .insert(memberObj)

    if (memberError) {
      console.error('Error al asignar administrador:', memberError)
      throw new Error(`Error al asignar administrador: ${memberError.message}`)
    }

    // 8. Éxito
    setSuccess(true)

    // Resetear formulario
    setForm({
      name: '',
      cif: '',
      phone: '',
      email: '',
      location: '',
      color: '',
      icon: null
    })
    setIconPreview(null)

    navigate(`/office/${officeData.id}/home`);
  }

  const handleRegisterOffice = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(false)

    try {
      if (isEditMode) {
        await handleUpdateOffice()
      } else {
        await handleCreateOffice()
      }
    } catch (error) {
      console.error('Error en handleRegisterOffice:', error)
      setError(error.message || 'Error al registrar la oficina')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      <Box sx={{ 
        maxWidth: { xs: '100%', sm: 600, md: 700 }, 
        mx: 'auto', 
        px: { xs: 1, sm: 2 } 
      }}>
        <Paper 
          elevation={3}
          sx={{ 
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 2
          }}
        >
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            gutterBottom 
            fontWeight="bold"
            sx={{ 
              textAlign: { xs: 'center', sm: 'left' },
              mb: { xs: 2, sm: 3 }
            }}
          >
            {isEditMode ? 'Editar despacho' : 'Crear nuevo despacho'}
          </Typography>

          {/* Mensajes de estado */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {isEditMode ? '¡Despacho actualizado exitosamente!' : '¡Despacho creado exitosamente!'}
            </Alert>
          )}

          {loadingOffice ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
          <form onSubmit={handleRegisterOffice}>
            <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              <TextField
                label="Nombre del despacho"
                fullWidth
                required
                value={form.name}
                onChange={handleChange('name')}
                size={isMobile ? "small" : "medium"}
                disabled={loading}
              />

              <TextField
                label="NIF o CIF"
                fullWidth
                required
                value={form.cif}
                onChange={handleChange('cif')}
                size={isMobile ? "small" : "medium"}
                disabled={loading}
              />

              <TextField
                label="Teléfono de contacto"
                fullWidth
                required
                type="tel"
                value={form.phone}
                onChange={handleChange('phone')}
                size={isMobile ? "small" : "medium"}
                disabled={loading}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*'
                }}
              />

              <TextField
                label="Correo corporativo"
                fullWidth
                required
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                size={isMobile ? "small" : "medium"}
                disabled={loading}
              />

              <TextField
                label="Ubicación"
                fullWidth
                placeholder="Ej. Madrid · C. de la Castellana"
                value={form.location}
                onChange={handleChange('location')}
                size={isMobile ? "small" : "medium"}
                disabled={loading}
              />

              <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 1.5,
                    fontWeight: 500,
                    color: 'text.secondary'
                  }}
                >
                  Icono del despacho (opcional)
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 2
                }}>
                  <Button 
                    variant="outlined" 
                    component="label" 
                    startIcon={<UploadFileIcon />}
                    size={isMobile ? "small" : "medium"}
                    disabled={loading}
                    sx={{ 
                      minWidth: { xs: '100%', sm: 'auto' },
                      color: '#3d475d'
                    }}
                  >
                    Subir ícono
                    <input 
                      type="file" 
                      hidden 
                      onChange={handleFileChange} 
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" 
                    />
                  </Button>
                  {iconPreview && (
                    <Avatar
                      src={iconPreview}
                      alt="icono preview"
                      sx={{ 
                        width: { xs: 56, sm: 64 }, 
                        height: { xs: 56, sm: 64 },
                        alignSelf: { xs: 'center', sm: 'flex-start' }
                      }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Formatos permitidos: JPG, PNG, GIF, WEBP. Tamaño máximo: 2MB
                </Typography>
              </Box>

              <FormControl fullWidth required>
                <InputLabel 
                  id="color-select-label"
                  size={isMobile ? "small" : "normal"}
                >
                  Color principal
                </InputLabel>
                <Select
                  labelId="color-select-label"
                  value={form.color === customColor ? 'custom' : form.color}
                  label="Color principal"
                  onChange={handleChange('color')}
                  size={isMobile ? "small" : "medium"}
                  disabled={loading}
                >
                  {colorOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box
                        sx={{
                          display: 'inline-block',
                          width: { xs: 14, sm: 16 },
                          height: { xs: 14, sm: 16 },
                          backgroundColor: option.value === 'custom' ? customColor : option.value,
                          borderRadius: '50%',
                          mr: { xs: 1, sm: 1.5 },
                          border: option.value === '#000000' ? '1px solid #ccc' : 'none'
                        }}
                      />
                      <Typography variant="body2" component="span">
                        {option.label}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {form.color === customColor && (
                <Box sx={{ mt: 2 }}>
                  <Typography 
                    variant="body2" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 500,
                      color: 'text.secondary',
                      mb: 2
                    }}
                  >
                    Elige un color personalizado:
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                    '& .chrome-picker': {
                      width: { xs: '100% !important', sm: 'auto !important' },
                      maxWidth: { xs: '280px !important', sm: 'none !important' }
                    }
                  }}>
                    <ChromePicker
                      color={customColor}
                      onChange={handleCustomColorChange}
                      disableAlpha
                    />
                  </Box>
                </Box>
              )}

              <Button 
                type="submit" 
                variant="contained" 
                //color="primary" 
                fullWidth
                size={isMobile ? "medium" : "large"}
                disabled={loading}
                sx={{ 
                  mt: { xs: 3, sm: 4 },
                  py: { xs: 1.5, sm: 2 },
                  fontWeight: 600,
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  backgroundColor: '#3d475d'
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    {isEditMode ? 'Guardando cambios...' : 'Creando despacho...'}
                  </>
                ) : (
                  isEditMode ? 'Guardar cambios' : 'Crear despacho'
                )}
              </Button>
            </Stack>
          </form>
          )}
        </Paper>
      </Box>
    </Container>
  )
}

export default OfficeRegisterView